// Constants
import CONST_STORAGE from 'constants/storage';
import CONST_TRIPS from 'constants/trips';

// Helpers
import { parseDMMToDD } from 'helpers/map';
import { getJsonData } from 'helpers/getJsonData';
import { formatDateTimezone } from 'helpers/formatDateTimezone';

// Types
import { TripInterface } from 'store/ducks/trips/types';
import { IMonitoringData } from 'common/monitoring.interface';
import { TripsOfResumeItineraryVesselsInterface } from 'store/ducks/itineraries/types';

interface ItineraryListInterface
  extends TripsOfResumeItineraryVesselsInterface {
  thereAreVacancies: boolean;
}

export async function getTripRoute(url: string) {
  const tripRoute = await getJsonData(url);

  if (!tripRoute) return null;

  return tripRoute.map(
    ({ lat, lng, timestamp, alreadyParsed, ...rest }: any) => {
      const coordinates = alreadyParsed ? { lat, lng } : parseDMMToDD(lat, lng);
      return {
        ...coordinates,
        timestamp: timestamp * 1000, // to milliseconds
        ...rest,
      };
    },
  ) as IMonitoringData[];
}

export const setTripId = (id: string) =>
  localStorage.setItem(CONST_STORAGE.TRIP.CURRENT_TRIP_ID, `${id}`);

export const getTripId = () =>
  localStorage.getItem(CONST_STORAGE.TRIP.CURRENT_TRIP_ID);

export const removeTripId = () =>
  localStorage.removeItem(CONST_STORAGE.TRIP.CURRENT_TRIP_ID);

export const getTripOccurrence = (trip: TripInterface, lastPoint?: boolean) => {
  const position = lastPoint
    ? trip.LineSection?.Line?.LineStops?.length! - 1
    : 0;
  const lineStopId = trip.LineSection?.Line?.LineStops?.[position]?.id;
  return trip?.TripOccurrences?.find(
    (occurrence) => occurrence.lineStopId === lineStopId,
  );
};

export const getTripOccurrenceStatus = (
  trip: TripInterface,
  lastPoint?: boolean,
) => {
  const type = getTripOccurrence(trip, lastPoint)?.type;
  const status = Object.entries(CONST_TRIPS.TRIP.OCCURRENCES)
    .map(([_, occurrence]) => occurrence)
    .find(({ key }) => key === type)?.label;

  return status || 'ok';
};

export const formatDatetime = (
  trip: TripInterface,
  type: string,
  lastPoint?: boolean,
) =>
  // @ts-ignore
  getTripOccurrence(trip, lastPoint)?.[type]
    ? formatDateTimezone(
      // @ts-ignore
      getTripOccurrence(trip, lastPoint)?.[type]!,
      'dd/MM/yyyy • HH:mm:ss',
    )
    : '--';

export const getTripOccurrencesQuantityByType = (
  trip: TripInterface,
  type: number,
  stopId?: string,
) => {
  if (stopId) {
    return (
      trip?.TripOccurrences?.filter(
        (occurrence) =>
          occurrence.type === type && occurrence.lineStopId === stopId,
      ).length || 0
    );
  }
  return (
    trip?.TripOccurrences?.filter((occurrence) => occurrence.type === type)
      .length || 0
  );
};

export const getTripOccurrencesByType = (trip: TripInterface, type: number) =>
  trip?.TripOccurrences?.filter((occurrence) => occurrence.type === type);

export const getTotalSeatResavation = (trip: ItineraryListInterface) => {
  if (
    !Array.isArray(trip?.TripScheduleProfileReservations) ||
    trip?.TripScheduleProfileReservations?.length === 0
  ) {
    return '';
  }
  const totalResvated = trip.TripScheduleProfileReservations.reduce(
    (acc, obj) => {
      const totalResvatedCapacity =
        obj.quantity -
        trip.Tokens.filter((e) => e.profileId === obj.profileId).length;
      if (totalResvatedCapacity < 0) return acc;
      return acc + totalResvatedCapacity;
    },
    0,
  );

  return ` (${Number(trip?.footCapacity ?? 0) -
    totalResvated} padrão, ${trip?.TripScheduleProfileReservations.map(
      (obj) => {
        const totalResvatedCapacity =
          obj.quantity -
          trip.Tokens.filter((e) => e.profileId === obj.profileId).length;
        return `${totalResvatedCapacity < 0 ? 0 : totalResvatedCapacity} ${obj.profileName
          }`;
      },
    ).join(', ')})`;
};
